import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import ClientPortalLayout from '@/components/client/ClientPortalLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Calendar, Clock, MapPin, FileText, Plus, Edit, Repeat, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import ScheduleAppointmentDialog from '@/components/client/ScheduleAppointmentDialog';
import ModifyAppointmentDialog from '@/components/client/ModifyAppointmentDialog';

interface Appointment {
  id: string;
  name: string;
  email: string;
  phone: string;
  service_type: string;
  appointment_type: string;
  preferred_date: string | null;
  preferred_time: string | null;
  preferred_timezone: string | null;
  notification_preference: string | null;
  is_recurring: boolean | null;
  recurrence_pattern: any | null;
  parent_appointment_id: string | null;
  modification_request: any | null;
  modification_status: string | null;
  status: string;
  message: string | null;
  created_at: string;
}

export default function ClientAppointments() {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [modifyAppt, setModifyAppt] = useState<Appointment | null>(null);
  const [clientData, setClientData] = useState<{
    id: string;
    full_name: string;
    email: string;
    phone: string | null;
  } | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      try {
        // Get full client record
        const { data: clientRecord, error: clientError } = await supabase
          .from('clients')
          .select('id, full_name, email, phone')
          .eq('user_id', user.id)
          .maybeSingle();

        if (clientError) throw clientError;

        if (clientRecord) {
          setClientData(clientRecord);
          
          // Fetch appointments
          const { data, error } = await supabase
            .from('appointments')
            .select('*')
            .or(`client_id.eq.${clientRecord.id},email.eq.${user.email}`)
            .order('created_at', { ascending: false });

          if (error) throw error;
          setAppointments(data || []);
        } else {
          // No client record, just fetch by email
          const { data, error } = await supabase
            .from('appointments')
            .select('*')
            .eq('email', user.email)
            .order('created_at', { ascending: false });

          if (error) throw error;
          setAppointments(data || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Set up real-time subscription
    const channel = supabase
      .channel('client-appointments')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'appointments' }, () => {
        fetchData();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      pending: "secondary",
      confirmed: "default",
      completed: "outline",
      cancelled: "destructive",
    };
    return <Badge variant={variants[status] || "default"}>{status}</Badge>;
  };

  const getServiceLabel = (serviceType: string) => {
    const labels: Record<string, string> = {
      individual: "Individual Tax Return",
      "sole-proprietor": "Sole Proprietor / Schedule C",
      amended: "Amended Return",
      consultation: "Tax Consultation",
    };
    return labels[serviceType] || serviceType;
  };

  return (
    <ClientPortalLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">My Appointments</h1>
          <p className="text-muted-foreground mt-2">
            View and manage your appointments with Tiny Tax Lady
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading appointments...</p>
          </div>
        ) : appointments.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Appointments Yet</h3>
              <p className="text-muted-foreground mb-4">
                You haven't scheduled any appointments yet.
              </p>
              <Button 
                onClick={() => setShowScheduleDialog(true)}
                className="w-full sm:w-auto"
                disabled={!clientData}
              >
                Schedule your first appointment
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <div>
                <h2 className="text-2xl font-bold">Your Appointments</h2>
                <p className="text-muted-foreground mt-1">
                  View and manage your upcoming and past appointments
                </p>
              </div>
              {clientData && (
                <Button onClick={() => setShowScheduleDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Appointment
                </Button>
              )}
            </div>
          <div className="space-y-4">
            {appointments.map((appointment) => (
              <Card key={appointment.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl">
                        {getServiceLabel(appointment.service_type)}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        Requested on {format(new Date(appointment.created_at), 'PPP')}
                      </CardDescription>
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                      {getStatusBadge(appointment.status)}
                      
                      {appointment.is_recurring && (
                        <Badge variant="outline" className="gap-1">
                          <Repeat className="h-3 w-3" />
                          {appointment.parent_appointment_id ? 'Part of series' : `Series (${appointment.recurrence_pattern?.count || 0})`}
                        </Badge>
                      )}
                      
                      {appointment.modification_status === 'pending' && (
                        <Badge variant="secondary" className="gap-1">
                          <AlertCircle className="h-3 w-3" />
                          Change Requested
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {appointment.modification_status === 'pending' && appointment.modification_request && (
                    <Alert className="mb-4 border-orange-200 bg-orange-50">
                      <AlertCircle className="h-4 w-4 text-orange-600" />
                      <AlertDescription>
                        You requested to change this appointment to{' '}
                        {format(new Date(appointment.modification_request.requested_date), 'MMM d, yyyy')} at{' '}
                        {appointment.modification_request.requested_time}.
                        Waiting for admin approval.
                      </AlertDescription>
                    </Alert>
                  )}
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="flex items-start gap-3">
                      <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">Preferred Date</p>
                        <p className="text-sm text-muted-foreground">
                          {appointment.preferred_date
                            ? format(new Date(appointment.preferred_date), 'PPP')
                            : 'Not specified'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">Type</p>
                        <p className="text-sm text-muted-foreground">
                          {appointment.appointment_type === 'in-person'
                            ? 'In-Person (Lenexa, KS)'
                            : 'Virtual Meeting'}
                        </p>
                      </div>
                    </div>

                    {appointment.appointment_type === 'virtual' && appointment.preferred_timezone && (
                      <div className="flex items-start gap-3">
                        <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm font-medium">Timezone</p>
                          <p className="text-sm text-muted-foreground">
                            {appointment.preferred_timezone.split('/')[1].replace('_', ' ')}
                          </p>
                        </div>
                      </div>
                    )}

                    {appointment.message && (
                      <div className="flex items-start gap-3 md:col-span-2">
                        <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm font-medium">Notes</p>
                          <p className="text-sm text-muted-foreground">{appointment.message}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {(appointment.status === 'pending' || appointment.status === 'confirmed') && 
                   !appointment.modification_status && (
                    <div className="mt-4 pt-4 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setModifyAppt(appointment)}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Request Change
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          </>
        )}

        {/* Schedule Appointment Dialog */}
        {clientData && (
          <ScheduleAppointmentDialog
            open={showScheduleDialog}
            onOpenChange={setShowScheduleDialog}
            clientData={clientData}
          />
        )}

        {/* Modify Appointment Dialog */}
        {modifyAppt && (
          <ModifyAppointmentDialog
            appointment={modifyAppt}
            open={!!modifyAppt}
            onOpenChange={(open) => !open && setModifyAppt(null)}
          />
        )}
      </div>
    </ClientPortalLayout>
  );
}
